import type {
  AlertRule,
  ApiToken,
  ApiTokenCreated,
  ArtifactSummary,
  ArtifactUsage,
  AlertRuleInput,
  AuthOptions,
  LoginResult,
  MfaChallenge,
  MfaStatus,
  Passkey,
  PasskeyAuthenticationOptions,
  PasskeyRegistered,
  PasskeyRegistrationOptions,
  RecoveryCodes,
  TotpConfirmed,
  TotpEnrolment,
  Bucket,
  EventTotal,
  Integration,
  IntegrationInput,
  Issue,
  IssueActivity,
  IssueComment,
  IssueSort,
  Invite,
  InviteCreated,
  InviteDetails,
  IssueStatus,
  OrgMember,
  OrgRole,
  Page,
  Project,
  ProjectKey,
  ProjectMember,
  PurgedIssues,
  ReleaseSummary,
  Session,
  StatsToken,
  StatsTokenCreated,
  StoredEvent,
  Team,
  TeamMember,
  TeamSummary,
  User,
  UserOrganization,
  ApiErrorBody,
  ConsentDetails,
  ConsentResult,
  ConnectedApplication,
  OrganizationSso,
  SsoRedirect,
  UserIdentity,
} from './types';

/**
 * Whether signing in stopped to ask for a second factor.
 *
 * A narrowing helper rather than a field check at every call site: the two
 * shapes are distinguished by the server's `mfa_required` marker.
 */
export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return 'mfa_required' in result && result.mfa_required;
}

/** Thrown for any non-2xx response, carrying the server's machine-readable code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when re-authenticating might help. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * True when the organization requires a second factor and this session was
   * established without one. Distinct from an ordinary refusal: there is
   * something the user can do about it.
   */
  get isMfaRequired(): boolean {
    return this.code === 'mfa_required';
  }

  /**
   * True when the organization requires single sign-on and this session was
   * not established at one of its providers. Separate from
   * {@link isMfaRequired} because the remedy is a different button.
   */
  get isSsoRequired(): boolean {
    return this.code === 'sso_required';
  }
}

export interface ClientOptions {
  /**
   * Where the API is, e.g. `https://outpost.example.com`. Empty, or a path
   * without a host, means the origin the page was served from — which is how
   * the dashboard runs behind an ingress or the Vite dev proxy.
   */
  baseUrl: string;
  /** Returns the current access token, or null when signed out. */
  getToken: () => string | null;
  /**
   * Called once on 401 to obtain a fresh token. Returning null signs the user
   * out. Concurrent 401s share a single refresh.
   */
  onUnauthorized?: () => Promise<string | null>;
}

export class OutpostClient {
  private refreshing: Promise<string | null> | null = null;

  constructor(private options: ClientOptions) {}

  // --- auth ---------------------------------------------------------------

  /**
   * Sign in with a password.
   *
   * Answers with a session, or — when the account has an authenticator — a
   * challenge to complete at {@link completeMfa}. Use {@link isMfaChallenge} to
   * tell them apart.
   */
  login(email: string, password: string): Promise<LoginResult> {
    return this.request('POST', '/auth/login', { body: { email, password }, anonymous: true });
  }

  /** Finish a sign-in with an authenticator code or a recovery code. */
  completeMfa(mfaToken: string, code: string): Promise<Session> {
    return this.request('POST', '/auth/login/mfa', {
      body: { mfa_token: mfaToken, code },
      anonymous: true,
    });
  }

  // --- second factors -------------------------------------------------------

  mfaStatus(): Promise<MfaStatus> {
    return this.request('GET', '/auth/mfa');
  }

  /** Begin enrolling an authenticator. Nothing changes until a code confirms it. */
  startTotpEnrolment(): Promise<TotpEnrolment> {
    return this.request('POST', '/auth/mfa/totp');
  }

  /** Enable the authenticator. The recovery codes are shown only here. */
  confirmTotp(code: string): Promise<TotpConfirmed> {
    return this.request('POST', '/auth/mfa/totp/confirm', { body: { code } });
  }

  /** Remove the authenticator. Needs a current code, or a recovery code. */
  disableTotp(code: string): Promise<void> {
    return this.request('POST', '/auth/mfa/totp/disable', { body: { code } });
  }

  regenerateRecoveryCodes(code: string): Promise<RecoveryCodes> {
    return this.request('POST', '/auth/mfa/recovery-codes', { body: { code } });
  }

  // --- passkeys -------------------------------------------------------------

  listPasskeys(): Promise<Passkey[]> {
    return this.request('GET', '/auth/passkeys');
  }

  passkeyRegistrationOptions(): Promise<PasskeyRegistrationOptions> {
    return this.request('POST', '/auth/passkeys/options');
  }

  /** Register the credential the browser produced. */
  registerPasskey(name: string, credential: unknown): Promise<PasskeyRegistered> {
    return this.request('POST', '/auth/passkeys', { body: { name, credential } });
  }

  deletePasskey(passkeyId: string): Promise<void> {
    return this.request('DELETE', `/auth/passkeys/${passkeyId}`);
  }

  /** Options for signing in with a passkey. Needs no session, by definition. */
  passkeyLoginOptions(): Promise<PasskeyAuthenticationOptions> {
    return this.request('POST', '/auth/passkeys/login/options', { anonymous: true });
  }

  /** Sign in with the assertion the browser produced. */
  passkeyLogin(credential: unknown): Promise<Session> {
    return this.request('POST', '/auth/passkeys/login', { body: credential, anonymous: true });
  }

  // --- single sign-on -------------------------------------------------------

  /**
   * Begin signing in at an identity provider.
   *
   * Answers with the URL to send the browser to and the state that must come
   * back with it. Nothing is signed in until {@link completeSso}.
   */
  startSso(provider: string): Promise<SsoRedirect> {
    return this.request('POST', `/auth/sso/${provider}/start`, { anonymous: true });
  }

  /**
   * Finish a sign-in with the code the provider redirected back with.
   *
   * Answers like {@link login}: a session, or — when the account has an
   * authenticator — a challenge to complete at {@link completeMfa}.
   */
  completeSso(code: string, state: string): Promise<LoginResult> {
    return this.request('POST', '/auth/sso/callback', {
      body: { code, state },
      anonymous: true,
    });
  }

  /** Begin connecting a provider to the signed-in account. */
  startSsoConnect(provider: string): Promise<SsoRedirect> {
    return this.request('POST', `/auth/sso/${provider}/connect`);
  }

  /** Finish connecting a provider. Answers with the account's identities. */
  completeSsoConnect(code: string, state: string): Promise<UserIdentity[]> {
    return this.request('POST', '/auth/sso/connect/callback', { body: { code, state } });
  }

  listSsoIdentities(): Promise<UserIdentity[]> {
    return this.request('GET', '/auth/sso/identities');
  }

  /** Disconnect a provider. Refused when it is the only way into the account. */
  disconnectSso(provider: string): Promise<void> {
    return this.request('DELETE', `/auth/sso/identities/${provider}`);
  }

  /** An organization's providers and whether it requires one. */
  organizationSso(orgId: string): Promise<OrganizationSso> {
    return this.request('GET', `/organizations/${orgId}/sso`);
  }

  /** Accept or stop accepting one provider for an organization. */
  setOrganizationSsoProvider(
    orgId: string,
    provider: string,
    enabled: boolean,
  ): Promise<OrganizationSso> {
    return this.request('PUT', `/organizations/${orgId}/sso/${provider}`, { body: { enabled } });
  }

  register(input: {
    email: string;
    name: string;
    password: string;
    organization?: string;
  }): Promise<Session> {
    return this.request('POST', '/auth/register', { body: input, anonymous: true });
  }

  refresh(refreshToken: string): Promise<Session> {
    return this.request('POST', '/auth/refresh', {
      body: { refresh_token: refreshToken },
      anonymous: true,
    });
  }

  logout(refreshToken: string): Promise<void> {
    return this.request('POST', '/auth/logout', { body: { refresh_token: refreshToken } });
  }

  me(): Promise<User> {
    return this.request('GET', '/auth/me');
  }

  authOptions(): Promise<AuthOptions> {
    return this.request('GET', '/auth/options', { anonymous: true });
  }

  // --- organizations and projects ----------------------------------------

  listOrganizations(): Promise<UserOrganization[]> {
    return this.request('GET', '/organizations');
  }

  createOrganization(name: string): Promise<UserOrganization> {
    return this.request('POST', '/organizations', { body: { name } });
  }

  /**
   * Rename an organization, or change whether it requires a second factor or
   * single sign-on. Its slug stays the same either way.
   */
  updateOrganization(
    orgId: string,
    update: { name?: string; require_mfa?: boolean; require_sso?: boolean },
  ): Promise<UserOrganization> {
    return this.request('PATCH', `/organizations/${orgId}`, { body: update });
  }

  /** The email domains an organization allows. Readable by every member. */
  getAllowedDomains(orgId: string): Promise<string[]> {
    return this.request<{ domains: string[] }>(
      'GET',
      `/organizations/${orgId}/allowed-domains`,
    ).then((response) => response.domains);
  }

  /**
   * Replace the allowed email domains. Owners only.
   *
   * With none listed, anyone can be invited and alert emails go to members only.
   * With some, invitations are limited to them and alert emails may also go to
   * addresses at them.
   */
  setAllowedDomains(orgId: string, domains: string[]): Promise<string[]> {
    return this.request<{ domains: string[] }>('PUT', `/organizations/${orgId}/allowed-domains`, {
      body: { domains },
    }).then((response) => response.domains);
  }

  listMembers(orgId: string): Promise<OrgMember[]> {
    return this.request('GET', `/organizations/${orgId}/members`);
  }

  updateMemberRole(orgId: string, userId: string, role: OrgRole): Promise<void> {
    return this.request('PATCH', `/organizations/${orgId}/members/${userId}`, { body: { role } });
  }

  /** Remove someone from an organization; pass your own id to leave. */
  removeMember(orgId: string, userId: string): Promise<void> {
    return this.request('DELETE', `/organizations/${orgId}/members/${userId}`);
  }

  // --- invitations --------------------------------------------------------

  listInvites(orgId: string): Promise<Invite[]> {
    return this.request('GET', `/organizations/${orgId}/invites`);
  }

  /** The response carries the accept link, which cannot be retrieved again. */
  createInvite(
    orgId: string,
    input: { email: string; role: OrgRole; project_ids?: string[] },
  ): Promise<InviteCreated> {
    return this.request('POST', `/organizations/${orgId}/invites`, { body: input });
  }

  /** Issue a new link, replacing the old one, and email it again. */
  resendInvite(orgId: string, inviteId: string): Promise<InviteCreated> {
    return this.request('POST', `/organizations/${orgId}/invites/${inviteId}/resend`);
  }

  revokeInvite(orgId: string, inviteId: string): Promise<void> {
    return this.request('DELETE', `/organizations/${orgId}/invites/${inviteId}`);
  }

  /** Describe an invitation from its link. Needs no session: most invitees have no account. */
  lookupInvite(token: string): Promise<InviteDetails> {
    return this.request('POST', '/invites/lookup', { body: { token }, anonymous: true });
  }

  acceptInvite(token: string): Promise<UserOrganization> {
    return this.request('POST', '/invites/accept', { body: { token } });
  }

  /** Create an account for the invited address and join; returns a session. */
  registerWithInvite(input: { token: string; name: string; password: string }): Promise<Session> {
    return this.request('POST', '/invites/register', { body: input, anonymous: true });
  }

  listProjects(): Promise<Project[]> {
    return this.request('GET', '/projects');
  }

  getProject(projectId: string): Promise<Project> {
    return this.request('GET', `/projects/${projectId}`);
  }

  /** Stop accepting events for the project. Its issues and events stay readable. */
  archiveProject(projectId: string): Promise<Project> {
    return this.request('POST', `/projects/${projectId}/archive`);
  }

  unarchiveProject(projectId: string): Promise<Project> {
    return this.request('POST', `/projects/${projectId}/unarchive`);
  }

  /**
   * How many days a release may go without activity before its source maps are
   * deleted, or `null` to keep them forever. Requires `admin`.
   */
  setStaleReleaseDays(projectId: string, days: number | null): Promise<Project> {
    return this.request('PUT', `/projects/${projectId}/stale-release-days`, {
      body: { stale_release_days: days },
    });
  }

  /**
   * Delete every issue and event in the project, keeping the project, its keys,
   * releases and source maps. Irreversible; requires `owner`.
   */
  purgeIssues(projectId: string): Promise<PurgedIssues> {
    return this.request('POST', `/projects/${projectId}/issues/purge`);
  }

  /** Delete the project and all of its data. Irreversible; requires `owner`. */
  deleteProject(projectId: string): Promise<void> {
    return this.request('DELETE', `/projects/${projectId}`);
  }

  /** Hourly event counts across all of the project's issues. */
  projectStats(projectId: string, hours = 24): Promise<Bucket[]> {
    return this.request('GET', `/projects/${projectId}/stats`, { params: { hours } });
  }

  /** Every event the project has received, however long ago. */
  projectTotal(projectId: string): Promise<EventTotal> {
    return this.request('GET', `/projects/${projectId}/stats/total`);
  }

  /** Everyone who can access the project, assigned or through their role. */
  listProjectMembers(projectId: string): Promise<ProjectMember[]> {
    return this.request('GET', `/projects/${projectId}/members`);
  }

  addProjectMember(projectId: string, userId: string): Promise<void> {
    return this.request('POST', `/projects/${projectId}/members`, { body: { user_id: userId } });
  }

  removeProjectMember(projectId: string, userId: string): Promise<void> {
    return this.request('DELETE', `/projects/${projectId}/members/${userId}`);
  }

  createProject(orgId: string, name: string, platform?: string): Promise<Project> {
    return this.request('POST', `/organizations/${orgId}/projects`, { body: { name, platform } });
  }

  // --- teams --------------------------------------------------------------

  /** The organization's teams, each with its size and whether you are on it. */
  listTeams(orgId: string): Promise<TeamSummary[]> {
    return this.request('GET', `/organizations/${orgId}/teams`);
  }

  createTeam(orgId: string, name: string): Promise<Team> {
    return this.request('POST', `/organizations/${orgId}/teams`, { body: { name } });
  }

  getTeam(teamId: string): Promise<TeamSummary> {
    return this.request('GET', `/teams/${teamId}`);
  }

  /** Rename a team. Its slug stays the same. */
  updateTeam(teamId: string, name: string): Promise<Team> {
    return this.request('PATCH', `/teams/${teamId}`, { body: { name } });
  }

  /** Delete a team. Its projects stay; the access it granted does not. */
  deleteTeam(teamId: string): Promise<void> {
    return this.request('DELETE', `/teams/${teamId}`);
  }

  listTeamMembers(teamId: string): Promise<TeamMember[]> {
    return this.request('GET', `/teams/${teamId}/members`);
  }

  addTeamMember(teamId: string, userId: string): Promise<void> {
    return this.request('POST', `/teams/${teamId}/members`, { body: { user_id: userId } });
  }

  /** Take someone off a team; pass your own id to leave it. */
  removeTeamMember(teamId: string, userId: string): Promise<void> {
    return this.request('DELETE', `/teams/${teamId}/members/${userId}`);
  }

  listTeamProjects(teamId: string): Promise<Project[]> {
    return this.request('GET', `/teams/${teamId}/projects`);
  }

  /** Hand a project to a team, or to none at all with `null`. */
  setProjectTeam(projectId: string, teamId: string | null): Promise<Project> {
    return this.request('PUT', `/projects/${projectId}/team`, { body: { team_id: teamId } });
  }

  listKeys(projectId: string): Promise<ProjectKey[]> {
    return this.request('GET', `/projects/${projectId}/keys`);
  }

  createKey(projectId: string, label = 'default'): Promise<ProjectKey> {
    return this.request('POST', `/projects/${projectId}/keys`, { body: { label } });
  }

  revokeKey(projectId: string, keyId: string): Promise<void> {
    return this.request('DELETE', `/projects/${projectId}/keys/${keyId}`);
  }

  // --- MCP connections ----------------------------------------------------

  /** Describe a parked OAuth authorization request, for the consent screen. */
  getConsentRequest(requestId: string): Promise<ConsentDetails> {
    return this.request('GET', `/oauth/consent/${requestId}`);
  }

  /**
   * Approve or decline it. The response says where to send the browser.
   *
   * `allowWrite` also grants `outpost:write`, and is refused unless the
   * application asked for it.
   */
  decideConsent(requestId: string, approve: boolean, allowWrite = false): Promise<ConsentResult> {
    return this.request('POST', `/oauth/consent/${requestId}`, {
      body: { approve, allow_write: allowWrite },
    });
  }

  listConnectedApplications(): Promise<ConnectedApplication[]> {
    return this.request('GET', '/oauth/grants');
  }

  /** Disconnect an application. Takes effect on its very next request. */
  revokeConnectedApplication(grantId: string): Promise<void> {
    return this.request('DELETE', `/oauth/grants/${grantId}`);
  }

  // --- issues -------------------------------------------------------------

  listIssues(
    projectId: string,
    params: {
      query?: string;
      sort?: IssueSort;
      limit?: number;
      cursor?: string;
      hours?: number;
    } = {},
  ): Promise<Page<Issue>> {
    return this.request('GET', `/projects/${projectId}/issues`, { params });
  }

  getIssue(issueId: string): Promise<Issue> {
    return this.request('GET', `/issues/${issueId}`);
  }

  updateIssue(
    issueId: string,
    update: { status?: IssueStatus; ignore_minutes?: number; assignee_id?: string | null },
  ): Promise<Issue> {
    return this.request('PATCH', `/issues/${issueId}`, { body: update });
  }

  bulkUpdate(
    projectId: string,
    issueIds: string[],
    status: IssueStatus,
    ignoreMinutes?: number,
  ): Promise<{ updated: number }> {
    return this.request('POST', `/projects/${projectId}/issues/bulk`, {
      body: { issue_ids: issueIds, status, ignore_minutes: ignoreMinutes },
    });
  }

  latestEvent(issueId: string): Promise<StoredEvent> {
    return this.request('GET', `/issues/${issueId}/events/latest`);
  }

  listEvents(
    issueId: string,
    params: { limit?: number; before?: string } = {},
  ): Promise<StoredEvent[]> {
    return this.request('GET', `/issues/${issueId}/events`, { params });
  }

  issueStats(issueId: string, hours = 24): Promise<Bucket[]> {
    return this.request('GET', `/issues/${issueId}/stats`, { params: { hours } });
  }

  /** Who changed the issue's status or assignee, oldest first. */
  issueActivity(issueId: string): Promise<IssueActivity[]> {
    return this.request('GET', `/issues/${issueId}/activity`);
  }

  // --- comments -----------------------------------------------------------

  /** The whole discussion on an issue, oldest first. */
  listComments(issueId: string): Promise<IssueComment[]> {
    return this.request('GET', `/issues/${issueId}/comments`);
  }

  /**
   * Post a comment.
   *
   * `@Their Name` or `@their@address` in the body names someone; the server
   * records the mention and emails them, but only if it spells somebody who can
   * already see the project — and not if it spells two of them. Anything else
   * stays plain text.
   */
  addComment(issueId: string, body: string): Promise<IssueComment> {
    return this.request('POST', `/issues/${issueId}/comments`, { body: { body } });
  }

  /** Rewrite your own comment. Only the mentions the edit adds are notified. */
  updateComment(issueId: string, commentId: string, body: string): Promise<IssueComment> {
    return this.request('PATCH', `/issues/${issueId}/comments/${commentId}`, { body: { body } });
  }

  /** Delete a comment. Yours, or anyone's with the `admin` role. */
  deleteComment(issueId: string, commentId: string): Promise<void> {
    return this.request('DELETE', `/issues/${issueId}/comments/${commentId}`);
  }

  tagValues(projectId: string, key: string): Promise<string[]> {
    return this.request('GET', `/projects/${projectId}/tags/${encodeURIComponent(key)}/values`);
  }

  // --- releases and alerts -------------------------------------------------

  listReleases(projectId: string): Promise<ReleaseSummary[]> {
    return this.request('GET', `/projects/${projectId}/releases`);
  }

  /** What every release of the project has uploaded, added together. */
  getArtifactUsage(projectId: string): Promise<ArtifactUsage> {
    return this.request('GET', `/projects/${projectId}/releases/usage`);
  }

  /** The files uploaded for a release, sorted by name. */
  listArtifacts(projectId: string, releaseId: string): Promise<ArtifactSummary[]> {
    return this.request('GET', `/projects/${projectId}/releases/${releaseId}/artifacts`);
  }

  listAlertRules(projectId: string): Promise<AlertRule[]> {
    return this.request('GET', `/projects/${projectId}/alert-rules`);
  }

  getAlertRule(projectId: string, ruleId: string): Promise<AlertRule> {
    return this.request('GET', `/projects/${projectId}/alert-rules/${ruleId}`);
  }

  createAlertRule(projectId: string, rule: AlertRuleInput): Promise<AlertRule> {
    return this.request('POST', `/projects/${projectId}/alert-rules`, { body: rule });
  }

  // A full replacement, not a patch: the server rebuilds the rule from the body
  // and validates it as a whole, so a partial update would blank what it omits.
  updateAlertRule(projectId: string, ruleId: string, rule: AlertRuleInput): Promise<AlertRule> {
    return this.request('PUT', `/projects/${projectId}/alert-rules/${ruleId}`, { body: rule });
  }

  deleteAlertRule(projectId: string, ruleId: string): Promise<void> {
    return this.request('DELETE', `/projects/${projectId}/alert-rules/${ruleId}`);
  }

  /** Notification destinations, shared by every project in the organization. */
  listIntegrations(orgId: string): Promise<Integration[]> {
    return this.request('GET', `/organizations/${orgId}/integrations`);
  }

  createIntegration(orgId: string, integration: IntegrationInput): Promise<Integration> {
    return this.request('POST', `/organizations/${orgId}/integrations`, { body: integration });
  }

  // --- upload tokens -------------------------------------------------------

  /** The caller's own upload tokens in an organization; nobody else's are listed. */
  listApiTokens(orgId: string): Promise<ApiToken[]> {
    return this.request('GET', `/organizations/${orgId}/api-tokens`);
  }

  /**
   * The response carries the secret, which cannot be retrieved again. Omit
   * `expires_in_days`, or pass null, for a token that never expires.
   */
  createApiToken(
    orgId: string,
    input: { name: string; expires_in_days?: number | null },
  ): Promise<ApiTokenCreated> {
    return this.request('POST', `/organizations/${orgId}/api-tokens`, { body: input });
  }

  revokeApiToken(orgId: string, tokenId: string): Promise<void> {
    return this.request('DELETE', `/organizations/${orgId}/api-tokens/${tokenId}`);
  }

  // --- stats tokens --------------------------------------------------------

  /** Every live stats token in an organization, whoever created it. Admins only. */
  listStatsTokens(orgId: string): Promise<StatsToken[]> {
    return this.request('GET', `/organizations/${orgId}/stats-tokens`);
  }

  /**
   * The response carries the secret, which cannot be retrieved again. Omit
   * `expires_in_days`, or pass null, for a token that never expires.
   */
  createStatsToken(
    orgId: string,
    input: { name: string; expires_in_days?: number | null },
  ): Promise<StatsTokenCreated> {
    return this.request('POST', `/organizations/${orgId}/stats-tokens`, { body: input });
  }

  revokeStatsToken(orgId: string, tokenId: string): Promise<void> {
    return this.request('DELETE', `/organizations/${orgId}/stats-tokens/${tokenId}`);
  }

  // --- plumbing -----------------------------------------------------------

  private async request<T>(
    method: string,
    path: string,
    init: {
      body?: unknown;
      params?: Record<string, string | number | undefined>;
      anonymous?: boolean;
    } = {},
  ): Promise<T> {
    const response = await this.send(method, path, init);

    // Retry once behind a shared refresh, so a page issuing several parallel
    // requests does not trigger several refreshes (and invalidate its own
    // rotating refresh token in the process).
    if (response.status === 401 && !init.anonymous && this.options.onUnauthorized) {
      const token = await this.sharedRefresh();
      if (token) {
        return this.parse<T>(await this.send(method, path, init));
      }
    }

    return this.parse<T>(response);
  }

  private sharedRefresh(): Promise<string | null> {
    this.refreshing ??= this.options.onUnauthorized!().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private send(
    method: string,
    path: string,
    init: {
      body?: unknown;
      params?: Record<string, string | number | undefined>;
      anonymous?: boolean;
    },
  ): Promise<Response> {
    const url = this.url(path);
    for (const [key, value] of Object.entries(init.params ?? {})) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = {};
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';

    if (!init.anonymous) {
      const token = this.options.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      method,
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  }

  /**
   * Resolve an API path against the base URL.
   *
   * A base without a host is resolved against the page's own location. Outside
   * a browser there is no page to be relative to, so that is refused with a
   * message saying so rather than the bare "Invalid URL" `new URL` would give.
   */
  private url(path: string): URL {
    const target = `${this.options.baseUrl.replace(/\/+$/, '')}/v1${path}`;
    const page = globalThis.location?.href;
    try {
      return new URL(target, page);
    } catch {
      throw new Error(
        page
          ? `cannot build an API URL from base \`${this.options.baseUrl}\``
          : `baseUrl must be an absolute URL outside a browser, not \`${this.options.baseUrl}\``,
      );
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;

    if (!response.ok) {
      let body: ApiErrorBody = { error: response.statusText, code: 'unknown' };
      try {
        body = (await response.json()) as ApiErrorBody;
      } catch {
        // A non-JSON error (a proxy 502, say) still needs a usable message.
      }
      throw new ApiError(response.status, body.code, body.error);
    }

    return (await response.json()) as T;
  }
}
